/* @flow */
import * as _ from "lodash";

import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import { categoryIdsExtractor, displayUnitExtractor, priceInfoExtractor } from "./ArticleTrackingUtil";
import {
    getBundle,
    getDetailLink,
    getShowsSummaryDnrInfo,
    getStoreAvailability,
    createEvaluateArticlePriceAndPromotionInfo,
    isMarketplaceArticle,
    getCategoryPath
} from "shared/src/utils/articleUtil";
import {
    ArticleCardBundleRenderData,
    ArticleIdsRenderData,
    AvailabilityRenderData,
    Bundle,
    Offer
} from "shared/src/types/articlesearch/Article";
import { SellingPriceInfo } from "shared/src/types/EvaluateArticleNew";
import { ShoppingCartPosition } from "shared/src/types/ShoppingCartPosition";
import { ReplacementKind, TrackingTags } from "shared/src/tracking/Tracking";
import { isFsdShop } from "shared/src/utils/ShopFlavor";
import { BettyBundleStore } from "shared/src/types/EvaluateArticleNew";
import { ArticleRenderData } from "shared/src/types/RenderData";

type ArticleIdInput = {
    selectedBundleId: string;
    bettyOfferId: string | undefined | null;
    originalBundleId: string | undefined | null;
};

// FIXME: This function should not exist! We need to find a general way to deal with bettyBundleIds, variantIds, and
// bundleIds without any knowledge of their syntactic structure
export function articleVariantIdFromBundleId(id: string): {
    variantId: string;
    bundleId: string;
    bettyBundleId: string;
} {
    return {
        variantId: id.substr(0, id.length - 4),
        bundleId: id.substr(-4),
        bettyBundleId: id
    };
}

export function bettyBundleIdInsteadOfBundleNumber(
    bundles: {
        [x: string]: any;
    },
    bundleInformation: {
        [x: string]: any;
    }
): {
    [x: string]: any;
} {
    const expandedBundles: { [bundleId: string]: any } = {};
    for (const bundleId in bundles) {
        const bundle = bundles[bundleId];
        expandedBundles[bundle.bettyBundleId.bettyBundleId] = bundleInformation[bundle.bettyBundleId.bundleNumber];
    }
    return expandedBundles;
}

export function containsBettyBundleId(
    bettyBundleId: string | undefined | null,
    listOfArticles:
        | Array<{
              [x: string]: any;
          }>
        | undefined
        | null
): boolean {
    if (!listOfArticles) {
        return false;
    } else {
        return Boolean(_.find(listOfArticles, { bettyBundleId }));
    }
}

export function getOffersMatchingQuery(bundle: Bundle, query: string | undefined | null): string[] {
    if (!bundle.offers || !query) {
        return [];
    }
    return bundle.offers
        .filter((offer) => offer.offerId === query || "M" + offer.offerId === query)
        .map((offer) => offer.bettyOfferId);
}

export function getOfferAvailability(
    bundle: Bundle,
    storeData: BettyBundleStore,
    bettyOfferId: string | undefined | null,
    isEmployee: boolean
): AvailabilityRenderData {
    const offer: Offer | undefined | null = _.find(bundle.offers, (o) => o.bettyOfferId === bettyOfferId);
    if (!offer) {
        return createEmptyAvailabilityInfo();
    }

    const deliveryDate: string | undefined | null = _.get(
        offer,
        ["deliveryModes", storeData.selectedDeliveryMode || "", "leadtime", "deliveryDate"],
        null
    );

    return {
        availability: offer.availability,
        deliveryMode: storeData.selectedDeliveryMode,
        deliveryDate: deliveryDate ? new Date(deliveryDate) : null,
        showAvailability: isEmployee ? bundle.showEmployeeAvailability : bundle.showCustomerAvailability,
        delisted: false
    };
}

export function getArticleIds(
    article: ArticleRenderData,
    bundle: Bundle,
    isMarketplace: boolean,
    bettyOfferId: string | undefined | null,
    selectedBundleId: string,
    originalBundleId: string | undefined | null
): ArticleIdsRenderData {
    const articleVariantId = article.variantId && article.variantId.bettyVariantId;
    const basketId = isMarketplace ? bettyOfferId : selectedBundleId;
    const offer: Offer | undefined | null = _.find(bundle.offers, (o) => o.bettyOfferId === bettyOfferId);
    const displayId = isMarketplace && offer ? "M" + offer.offerId : bundle.customerDisplayId;
    return {
        articleVariantId,
        idForBasket: basketId,
        selectedBundleId: selectedBundleId, // For bundle selection
        forListActions: {
            newBundleId: bundle.bettyBundleId,
            bettyVariantId: articleVariantId,
            idForList: selectedBundleId,
            originalBundleId: originalBundleId
        },
        idForDisplay: displayId,
        trackingId: (bundle.trackingInfo && bundle.trackingInfo.trackingId) || "Unknown Id",
        offerId: offer ? offer.offerId : null,
        childBundleIds: bundle.refBundleIds && _.map(bundle.refBundleIds, (bundle) => bundle.bundleId)
    };
}

export function getTrackingInfo(
    article: ArticleRenderData,
    bundle: Bundle,
    priceAndPromotionInfo: SellingPriceInfo | undefined,
    replacementKind: ReplacementKind | undefined | null
): TrackingTags {
    const priceInfo = priceInfoExtractor(priceAndPromotionInfo);
    const displayUnit = displayUnitExtractor(bundle);
    const categoryIds = categoryIdsExtractor(bundle);

    const sanitizedCategoryPath = getCategoryPath(bundle);

    return {
        brandName: bundle.brandName,
        categoryIds: categoryIds,
        groupName: _.get(bundle, ["group", "groupName"]),
        mainGroupName: _.get(bundle, ["group", "mainGroupName"]),
        subGroupName: _.get(bundle, ["group", "subGroupName"]),
        position:
            "position" in article && article.position !== undefined && article.position !== null
                ? String(article.position)
                : null,
        promo: priceInfo.isPromo,
        customerIndividualPrice: priceInfo.customerIndividualPrice,
        basePrice: toString(priceInfo.basePrice),
        deliveryPrice: toString(priceInfo.deliveryPrice),
        displayUnit: displayUnit,
        variantName: article.name,
        categoryPath: sanitizedCategoryPath,
        subsysNumber: _.get(bundle, "refBundleIds[0].subsystemArticleNumber"),
        mgbNumber: _.get(bundle, "refBundleIds[0].mgbBundleNumber"),
        ownBrand: bundle.ownBrand,
        ropoRank: "ropoRank" in article ? article.ropoRank : null,
        showsSummaryDnrInfo: getShowsSummaryDnrInfo(priceAndPromotionInfo),
        ...(replacementKind && { replacementKind: replacementKind })
    };
}

function toString(x: number | null | undefined): string | null | undefined {
    if (typeof x === "number") {
        return String(x);
    } else {
        return x;
    }
}

/**
 * This filters out bundles from the bundle selector that are not customer searchable and not currently selected. When
 * bundles are viewable, but not searchable, that means customers should be able to see them when they have them e.g. on
 * a list, but should not be able to select them in the bundle selector.
 */
export function filterBundleSelectorForCustomer(
    article: ArticleRenderData,
    selectedStore: string | undefined | null,
    selectedBundleId: string | undefined | null
): {
    [x: string]: string;
} {
    const bundleSelector: { [x: string]: string } = {};
    for (const bundleId in article.bundleSelector) {
        const bundle = article.bundles[bundleId];
        const store = selectedStore && bundle && bundle.stores[selectedStore];
        let searchable = store && (store.customerSearchable || store.anonymousSearchable);
        // HACK: In countries like PT, we may get bundles that appear searchable because they are searchable for
        // STORE/STORE when using the full assortment, but we only want to show the FSD assortment. Ideally, this would
        // be handled by the search service because it knows which assortment we're looking for (the UI currently
        // doesn't have access to the `restrictShopAssortmentToFsd` flag). Since we can't do that yet, this is a hack to
        // hide those bundles; the hope is that !isMarioCountry matches up with restrictShopAssortmentToFsd (it
        // currently does).
        if (
            (!SessionStore.isMarioCountry() || isFsdShop()) &&
            !_.get(store, ["possibleDeliveryModes", "METRO_DELIVERY", "possibleFulfillmentTypes", "FSD"])
        ) {
            searchable = false;
        }
        const allowed = bundleId === selectedBundleId || searchable;
        if (allowed) {
            bundleSelector[bundleId] = article.bundleSelector[bundleId];
        }
    }
    return bundleSelector;
}

export function createArticleCardRenderData(
    article: ArticleRenderData,
    isEmployee: boolean,
    selectedStore: string | undefined | null,
    ids: ArticleIdInput,
    amountInBasket: number | undefined | null,
    cartItem: ShoppingCartPosition | undefined | null,
    replacementKind: ReplacementKind | undefined | null
): ArticleCardBundleRenderData {
    const selectedBundleId = ids.selectedBundleId;
    const bundle = getBundle<typeof article, Bundle>(article, selectedBundleId);
    const storeData = getBundleStoreData(article, selectedStore, selectedBundleId);
    if (!bundle || !storeData) {
        return createEmptyRenderData();
    }

    const bundleSelector = isEmployee
        ? article.bundleSelector
        : filterBundleSelectorForCustomer(article, selectedStore, ids.selectedBundleId);

    const isWeightArticle = bundle.isWeightArticle === "WEIGHT";
    const isMarketplace = isMarketplaceArticle(bundle);
    const bettyOfferId = ids.bettyOfferId;
    const offer: Offer | undefined | null = _.find(bundle.offers, (o) => o.bettyOfferId === bettyOfferId);

    const availabilityInfo = isMarketplace
        ? getOfferAvailability(bundle, storeData, bettyOfferId, isEmployee)
        : getStoreAvailability(bundle, storeData, isEmployee);

    const priceWithAllPromotionInfos = createEvaluateArticlePriceAndPromotionInfo(
        article,
        ids,
        selectedStore,
        {
            [bundle.bettyBundleId.bettyBundleId]: amountInBasket
        },
        cartItem
    );

    const idInformation = getArticleIds(
        article,
        bundle,
        isMarketplace,
        bettyOfferId,
        selectedBundleId,
        ids.originalBundleId
    );

    const trackingInfo = getTrackingInfo(article, bundle, storeData.sellingPriceInfo, replacementKind);

    const imageUrls = {
        bundle: bundle.imageUrl,
        bundleFallBack: bundle.fallbackImageUrl,
        variant: article.url
    };

    return {
        buyability: {
            customer: storeData.customerBuyable ?? false,
            employee: storeData.employeeBuyable ?? false,
            deliveryMode: storeData.selectedDeliveryMode ?? null
        },
        availabilityInfo,
        priceAndPromotionInfo: priceWithAllPromotionInfos,
        isWeightArticle,
        weightPerPiece: bundle.weightPerPiece
            ? { unit: bundle.weightPerPiece.uom, value: bundle.weightPerPiece.value }
            : null,
        netPieceWeight: bundle.content?.netPieceWeight,
        articleIds: idInformation,
        pvLink: getDetailLink(bundle, bettyOfferId),
        marketPlaceShopInfo: {
            shopId: offer ? offer.shopId : null,
            shopName: offer ? offer.shopName : ""
        },
        marketplace: isMarketplace,
        description: bundle.description,
        imageUrls: imageUrls,
        numberOfBundles: Object.keys(bundleSelector || {}).length,
        bundleSelector: bundleSelector || {},
        debugInfo: "debugInfo" in article && article.debugInfo ? article.debugInfo : null,
        ltrDebugInfo: "ltrDebugInfo" in article && article.ltrDebugInfo ? article.ltrDebugInfo : null,
        listInformation: {
            listId: ("listId" in article && article.listId) || null,
            listType: ("listType" in article && article.listType) || null,
            listName: ("listName" in article && article.listName) || null,
            listAmount: ("listAmount" in article && article.listAmount) || null
        },
        readOnly: ("readOnly" in article && article.readOnly) || false,
        trackingInfo: trackingInfo,
        minOrderQuantity: bundle.minOrderQuantity,
        recentlyAddedExpiration: bundle.recentlyAddedExpiration
    };
}

export function getBundleStoreData(
    variant: ArticleRenderData,
    selectedStore: string | undefined | null,
    selectedBundleId: string
): BettyBundleStore | null {
    const bundle = getBundle(variant, selectedBundleId);
    if (!bundle) {
        return null;
    }
    const storeData = selectedStore && bundle.stores ? bundle.stores[selectedStore] : null;
    if (!storeData) {
        return null;
    }
    return storeData as BettyBundleStore; // Cast storeData to BettyBundleStore
}

export function createEmptyRenderData(): ArticleCardBundleRenderData {
    return {
        buyability: {
            customer: true,
            employee: true,
            deliveryMode: null
        },
        availabilityInfo: createEmptyAvailabilityInfo(),
        priceAndPromotionInfo: {
            priceHiddenReason: null,
            valid: false
        },
        isWeightArticle: false,
        weightPerPiece: null,
        netPieceWeight: null,
        pvLink: { pathname: "/", query: undefined },
        marketPlaceShopInfo: {
            shopId: null,
            shopName: ""
        },
        marketplace: false,
        articleIds: {
            articleVariantId: "",
            idForBasket: null,
            selectedBundleId: "unknown Id",
            forListActions: {
                newBundleId: null,
                bettyVariantId: "",
                idForList: "",
                originalBundleId: null
            },
            idForDisplay: "",
            trackingId: "unknown Id",
            offerId: null,
            childBundleIds: null
        },
        description: "unknown",
        imageUrls: {},
        numberOfBundles: 1,
        bundleSelector: {},
        debugInfo: null,
        ltrDebugInfo: null,
        listInformation: {
            listId: null,
            listType: null,
            listName: null,
            listAmount: null
        },
        readOnly: false,
        trackingInfo: {},
        recentlyAddedExpiration: null
    };
}

export function createEmptyAvailabilityInfo() {
    return {
        deliveryMode: null,
        deliveryDate: null,
        availability: null,
        showAvailability: true,
        delisted: false
    };
}
